/**
 * Harmonia Design System - JavaScript
 * minaCorale / omoや545 Branding
 * 
 * Kokochiyoi Motion System
 * - Intersection Observer for scroll reveals
 * - Product card interactions
 * - Wishlist toggle
 */

(function() {
  'use strict';

  // ============================================================
  // SCROLL REVEAL ANIMATION
  // ============================================================

  const REVEAL_OPTIONS = {
    root: null,
    rootMargin: '0px 0px 50px 0px',
    threshold: 0
  };

  /**
   * Observe children of a .harmonia-stagger container individually.
   * Children entering the viewport in the same animation frame
   * receive stagger delays (80ms apart) for a cascading reveal effect.
   */
  const _observeStaggerChildren = (container) => {
    if (container.dataset.staggerInit) return;
    container.dataset.staggerInit = '1';

    let pending = [];
    let rafId = null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          pending.push(entry.target);
          observer.unobserve(entry.target);
        }
      });

      if (pending.length && !rafId) {
        rafId = requestAnimationFrame(() => {
          const batch = pending.slice();
          pending = [];
          rafId = null;

          batch.forEach((el, i) => {
            el.style.transitionDelay = (i * 80) + 'ms';
            el.classList.add('is-visible');
          });

          // Clean up inline transition-delay after animations complete
          const cleanup = batch.length * 80 + 600;
          setTimeout(() => {
            batch.forEach(el => { el.style.transitionDelay = ''; });
          }, cleanup);
        });
      }
    }, REVEAL_OPTIONS);

    Array.from(container.children).forEach(child => {
      observer.observe(child);
    });
  };

  const initScrollReveal = () => {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, REVEAL_OPTIONS);

    // Observe single animated elements (non-stagger)
    document.querySelectorAll('.harmonia-animate').forEach(el => {
      revealObserver.observe(el);
    });

    // For stagger containers, observe each child individually
    document.querySelectorAll('.harmonia-stagger').forEach(container => {
      _observeStaggerChildren(container);
    });
  };

  // ============================================================
  // PRODUCT CARD INTERACTIONS
  // ============================================================
  
  const initProductCards = () => {
    document.querySelectorAll('.harmonia-product-card').forEach(card => {
      const quickAdd = card.querySelector('.harmonia-product-card__quick-add');
      
      if (quickAdd) {
        // Show/hide quick add on hover
        card.addEventListener('mouseenter', () => {
          quickAdd.classList.add('is-visible');
        });
        
        card.addEventListener('mouseleave', () => {
          quickAdd.classList.remove('is-visible');
        });
        
        // Touch device support
        card.addEventListener('touchstart', () => {
          quickAdd.classList.toggle('is-visible');
        }, { passive: true });
      }
    });
  };

  // ============================================================
  // WISHLIST TOGGLE
  // ============================================================
  
  const initWishlist = () => {
    document.querySelectorAll('.harmonia-product-card__wishlist').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isLiked = btn.classList.toggle('is-liked');
        
        // Update icon (fill/stroke)
        const svg = btn.querySelector('svg');
        if (svg) {
          if (isLiked) {
            svg.style.fill = 'currentColor';
          } else {
            svg.style.fill = 'none';
          }
        }
        
        // Optional: Dispatch custom event for tracking/cart integration
        const productCard = btn.closest('.harmonia-product-card');
        if (productCard) {
          const productId = productCard.dataset.productId;
          const event = new CustomEvent('harmonia:wishlist', {
            detail: { productId, isLiked }
          });
          document.dispatchEvent(event);
        }
      });
    });
  };

  // ============================================================
  // BUTTON RIPPLE EFFECT (Optional enhancement)
  // ============================================================
  
  const initButtonRipple = () => {
    document.querySelectorAll('.harmonia-btn--ripple').forEach(btn => {
      btn.addEventListener('click', function(e) {
        const rect = this.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const ripple = document.createElement('span');
        ripple.className = 'harmonia-btn__ripple';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        
        this.appendChild(ripple);
        
        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });
  };

  // ============================================================
  // SMOOTH SCROLL (for anchor links)
  // ============================================================
  
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        
        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  };

  // ============================================================
  // LAZY LOAD IMAGES (Native lazy loading fallback)
  // ============================================================
  
  const initLazyImages = () => {
    // Check if native lazy loading is supported
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading is supported
      document.querySelectorAll('img[data-src]').forEach(img => {
        img.src = img.dataset.src;
      });
    } else {
      // Fallback with Intersection Observer
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        });
      });
      
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  };

  // ============================================================
  // QUANTITY SELECTOR
  // ============================================================
  
  const initQuantitySelectors = () => {
    document.querySelectorAll('.harmonia-quantity').forEach(selector => {
      const minusBtn = selector.querySelector('.harmonia-quantity__minus');
      const plusBtn = selector.querySelector('.harmonia-quantity__plus');
      const input = selector.querySelector('.harmonia-quantity__input');
      
      if (!minusBtn || !plusBtn || !input) return;
      
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;
      
      minusBtn.addEventListener('click', () => {
        let value = parseInt(input.value) || min;
        if (value > min) {
          input.value = value - 1;
          input.dispatchEvent(new Event('change'));
        }
      });
      
      plusBtn.addEventListener('click', () => {
        let value = parseInt(input.value) || min;
        if (value < max) {
          input.value = value + 1;
          input.dispatchEvent(new Event('change'));
        }
      });
      
      input.addEventListener('change', () => {
        let value = parseInt(input.value) || min;
        value = Math.max(min, Math.min(max, value));
        input.value = value;
      });
    });
  };

  // ============================================================
  // ACCORDION
  // ============================================================

  const initAccordions = () => {
    document.querySelectorAll('.harmonia-accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', function() {
        const accordion = this.closest('.harmonia-accordion');
        const content = accordion.querySelector('.harmonia-accordion__content');
        const isOpen = accordion.classList.toggle('is-open');

        this.setAttribute('aria-expanded', isOpen);
        content.hidden = !isOpen;

        // Animate height
        if (isOpen) {
          content.style.height = content.scrollHeight + 'px';
        } else {
          content.style.height = '0';
        }
      });
    });
  };

  // ============================================================
  // SPRING PHYSICS ANIMATION (Kokochiyoi Motion)
  // Matches Framer Motion spring behavior for authentic feel
  // ============================================================

  /**
   * Spring animation using requestAnimationFrame
   *
   * @param {Object} params - Animation parameters
   * @param {number} params.from - Start value
   * @param {number} params.to - End value
   * @param {number} params.stiffness - Spring stiffness (default: 400)
   * @param {number} params.damping - Damping ratio (default: 17)
   * @param {number} params.mass - Mass (default: 1)
   * @param {Function} params.onUpdate - Callback with current value
   * @param {Function} params.onComplete - Callback on animation end
   * @returns {Function} Cancel function
   */
  const springAnimate = ({ from, to, stiffness = 400, damping = 17, mass = 1, onUpdate, onComplete }) => {
    let velocity = 0;
    let current = from;
    const target = to;

    let animationId;
    let lastTime = performance.now();

    const tick = (currentTime) => {
      const deltaTime = Math.min((currentTime - lastTime) / 1000, 0.064);
      lastTime = currentTime;

      // Spring physics calculation
      const displacement = current - target;
      const springForce = -stiffness * displacement;
      const dampingForce = -damping * velocity;
      const acceleration = (springForce + dampingForce) / mass;

      velocity += acceleration * deltaTime;
      current += velocity * deltaTime;

      onUpdate(current);

      // Check if animation should stop
      const isSettled = Math.abs(velocity) < 0.01 && Math.abs(displacement) < 0.001;

      if (isSettled) {
        onUpdate(target);
        if (onComplete) onComplete();
      } else {
        animationId = requestAnimationFrame(tick);
      }
    };

    animationId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationId);
  };

  // ============================================================
  // SPRING BUTTON ANIMATIONS
  // DS Reference: stiffness: 400, damping: 17, scale: 1.05/0.95
  // ============================================================

  const initSpringButtons = () => {
    const BUTTON_SPRING = { stiffness: 400, damping: 17 };

    document.querySelectorAll('.harmonia-btn').forEach(btn => {
      // Skip buttons inside product cards (handled separately)
      if (btn.closest('.harmonia-product-card__quick-add')) return;

      let cancelAnimation = null;
      let currentScale = 1;
      // React DS: Primary buttons get translateY(-2px) on hover (hover:-translate-y-0.5)
      // Other variants get scale only. Both are layered in React (outer motion.div + inner CSS)
      // In Liquid, we combine them on the same element.
      const isPrimary = btn.classList.contains('harmonia-btn--primary');
      const hoverY = isPrimary ? -2 : 0;

      const applyTransform = (scale, y) => {
        btn.style.transform = y !== 0 ? `scale(${scale}) translateY(${y}px)` : `scale(${scale})`;
      };

      btn.addEventListener('mouseenter', () => {
        if (cancelAnimation) cancelAnimation();
        cancelAnimation = springAnimate({
          from: currentScale,
          to: 1.05,
          ...BUTTON_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            applyTransform(scale, hoverY);
          }
        });
      });

      btn.addEventListener('mouseleave', () => {
        if (cancelAnimation) cancelAnimation();
        cancelAnimation = springAnimate({
          from: currentScale,
          to: 1,
          ...BUTTON_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            applyTransform(scale, 0);
          }
        });
      });

      btn.addEventListener('mousedown', () => {
        if (cancelAnimation) cancelAnimation();
        currentScale = 0.95;
        applyTransform(0.95, 0);
      });

      btn.addEventListener('mouseup', () => {
        cancelAnimation = springAnimate({
          from: 0.95,
          to: 1.05,
          ...BUTTON_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            applyTransform(scale, hoverY);
          }
        });
      });
    });
  };

  // ============================================================
  // QUICK ADD SPRING ANIMATIONS
  // DS Reference: stiffness: 400, damping: 12 (more bouncy)
  // ============================================================

  const initQuickAddSpring = () => {
    const QUICK_ADD_SPRING = { stiffness: 400, damping: 12 };

    document.querySelectorAll('.harmonia-product-card').forEach(card => {
      const quickAddBtn = card.querySelector('.harmonia-product-card__quick-add-btn');
      if (!quickAddBtn) return;

      let cancelScale = null;
      let cancelY = null;
      let currentScale = 1;
      let currentY = 0;

      const updateTransform = () => {
        quickAddBtn.style.transform = `scale(${currentScale}) translateY(${currentY}px)`;
      };

      card.addEventListener('mouseenter', () => {
        if (cancelScale) cancelScale();
        if (cancelY) cancelY();

        // Scale animation
        cancelScale = springAnimate({
          from: currentScale,
          to: 1.05,
          ...QUICK_ADD_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            updateTransform();
          }
        });

        // Y lift animation
        cancelY = springAnimate({
          from: currentY,
          to: -3,
          ...QUICK_ADD_SPRING,
          onUpdate: (y) => {
            currentY = y;
            updateTransform();
          }
        });

        // Add glow shadow with transition
        quickAddBtn.style.boxShadow = '0 10px 25px -5px rgba(255, 150, 0, 0.4)';
        quickAddBtn.style.filter = 'brightness(1.05)';
      });

      card.addEventListener('mouseleave', () => {
        if (cancelScale) cancelScale();
        if (cancelY) cancelY();

        cancelScale = springAnimate({
          from: currentScale,
          to: 1,
          ...QUICK_ADD_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            updateTransform();
          }
        });

        cancelY = springAnimate({
          from: currentY,
          to: 0,
          ...QUICK_ADD_SPRING,
          onUpdate: (y) => {
            currentY = y;
            updateTransform();
          }
        });

        // Remove glow
        quickAddBtn.style.boxShadow = '';
        quickAddBtn.style.filter = '';
      });

      // Button tap animation
      quickAddBtn.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        if (cancelScale) cancelScale();
        currentScale = 0.95;
        updateTransform();
      });

      quickAddBtn.addEventListener('mouseup', (e) => {
        e.stopPropagation();
        cancelScale = springAnimate({
          from: 0.95,
          to: 1.05,
          ...QUICK_ADD_SPRING,
          onUpdate: (scale) => {
            currentScale = scale;
            updateTransform();
          }
        });
      });
    });
  };

  // ============================================================
  // ZEN QUANTITY SELECTOR ENHANCEMENTS
  // 削除プロキシ + アンダーラインアニメーション
  // + fadeSlide数字アニメ + 長押し加速 + 削除コラプス
  // ============================================================

  const initZenQuantity = () => {

    // ── 1. 削除ボタン → 既存 .qty-adjuster__remove リンクをプロキシ ──
    document.addEventListener('click', (e) => {
      const deleteBtn = e.target.closest('.cart-summary-item__delete');
      if (!deleteBtn) return;

      const container = deleteBtn.closest('.cart-summary-item-container');
      if (!container) return;

      // コラプスアニメーション付き削除
      const currentHeight = container.offsetHeight;
      container.style.maxHeight = currentHeight + 'px';
      container.style.transition = 'none';
      // 次フレームでアニメーションクラスを付与
      requestAnimationFrame(() => {
        container.style.transition = '';
        container.classList.add('cart-summary-item-container--removing');
      });

      // アニメーション完了後に実際の削除を実行
      setTimeout(() => {
        const removeLink = container.querySelector('.qty-adjuster__remove');
        if (removeLink) removeLink.click();
      }, 350);
    });

    // ── 2. 数字の fadeSlide アニメーション（+/−ボタンクリック時） ──
    const triggerValueAnimation = (adjuster, direction) => {
      const valueEl = adjuster.querySelector('.qty-adjuster__value');
      if (!valueEl) return;

      // アンダーライン伸張（既存機能）
      valueEl.classList.add('qty-adjuster__value--changing');
      clearTimeout(valueEl._zenTimeout);
      valueEl._zenTimeout = setTimeout(() => {
        valueEl.classList.remove('qty-adjuster__value--changing');
      }, 300);

      // fadeSlide アニメーション（新規）
      // 既存アニメーションをリセットしてから再適用
      valueEl.classList.remove('qty-adjuster__value--animate-up', 'qty-adjuster__value--animate-down');
      // リフロー強制でアニメーション再トリガー
      void valueEl.offsetWidth;
      valueEl.classList.add(
        direction === 'up' ? 'qty-adjuster__value--animate-up' : 'qty-adjuster__value--animate-down'
      );

      // アニメーション終了後にクラス除去
      clearTimeout(valueEl._zenAnimTimeout);
      valueEl._zenAnimTimeout = setTimeout(() => {
        valueEl.classList.remove('qty-adjuster__value--animate-up', 'qty-adjuster__value--animate-down');
      }, 250);
    };

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty-adjuster--zen .qty-adjuster__down, .qty-adjuster--zen .qty-adjuster__up');
      if (!btn) return;
      const adjuster = btn.closest('.qty-adjuster--zen');
      if (!adjuster) return;

      const direction = btn.classList.contains('qty-adjuster__up') ? 'up' : 'down';
      triggerValueAnimation(adjuster, direction);
    });

    // ── 3. 直接入力時にもアンダーライン + アニメーションをトリガー ──
    document.addEventListener('input', (e) => {
      if (!e.target.matches('.qty-adjuster--zen .qty-adjuster__value')) return;
      e.target.classList.add('qty-adjuster__value--changing');
      clearTimeout(e.target._zenTimeout);
      e.target._zenTimeout = setTimeout(() => {
        e.target.classList.remove('qty-adjuster__value--changing');
      }, 300);
    });

    // ── 4. 長押し（ホールド）による加速連続増減 ──
    const HOLD_INITIAL_DELAY = 350; // 初回リピートまでの待ち時間(ms)
    const HOLD_ACCEL_FACTOR = 0.82; // 加速倍率（小さいほど速い）
    const HOLD_MIN_INTERVAL = 60;   // 最速インターバル(ms)

    const setupHoldBehavior = (btn) => {
      let holdTimer = null;
      let currentInterval = HOLD_INITIAL_DELAY;

      const doClick = () => {
        if (btn.disabled) {
          stopHold();
          return;
        }
        // Canopy の theme.js が監視している click イベントを発火
        btn.click();
      };

      const startHold = () => {
        if (btn.disabled) return;
        currentInterval = HOLD_INITIAL_DELAY;

        const tick = () => {
          doClick();
          currentInterval = Math.max(HOLD_MIN_INTERVAL, currentInterval * HOLD_ACCEL_FACTOR);
          holdTimer = setTimeout(tick, currentInterval);
        };

        // 最初の1クリックは通常のclickイベントで既に発火済み
        // 長押しの2回目以降からtickを開始
        holdTimer = setTimeout(tick, HOLD_INITIAL_DELAY);
      };

      const stopHold = () => {
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        currentInterval = HOLD_INITIAL_DELAY;
      };

      // Mouse events
      btn.addEventListener('mousedown', (e) => {
        // 左クリックのみ
        if (e.button !== 0) return;
        startHold();
      });
      btn.addEventListener('mouseup', stopHold);
      btn.addEventListener('mouseleave', stopHold);

      // Touch events
      btn.addEventListener('touchstart', () => {
        startHold();
      }, { passive: true });
      btn.addEventListener('touchend', stopHold);
      btn.addEventListener('touchcancel', stopHold);

      // ページ離脱時のクリーンアップ
      window.addEventListener('blur', stopHold);
    };

    // 既存のZen adjuster内のボタンにホールド動作を適用
    document.querySelectorAll('.qty-adjuster--zen').forEach(adjuster => {
      if (adjuster.dataset.zenHoldInit) return;
      adjuster.dataset.zenHoldInit = 'true';

      const downBtn = adjuster.querySelector('.qty-adjuster__down');
      const upBtn = adjuster.querySelector('.qty-adjuster__up');
      if (downBtn) setupHoldBehavior(downBtn);
      if (upBtn) setupHoldBehavior(upBtn);
    });

    // ── 5. カートドロワの動的コンテンツに対応（MutationObserver） ──
    // カートがAJAXで再描画された場合、新しいボタンにもホールド動作を適用
    const cartSummary = document.querySelector('.cart-summary');
    if (cartSummary) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            const adjusters = node.querySelectorAll
              ? node.querySelectorAll('.qty-adjuster--zen')
              : [];
            adjusters.forEach(adjuster => {
              if (adjuster.dataset.zenHoldInit) return;
              adjuster.dataset.zenHoldInit = 'true';
              const downBtn = adjuster.querySelector('.qty-adjuster__down');
              const upBtn = adjuster.querySelector('.qty-adjuster__up');
              if (downBtn) setupHoldBehavior(downBtn);
              if (upBtn) setupHoldBehavior(upBtn);
            });
          }
        }
      });
      observer.observe(cartSummary, { childList: true, subtree: true });
    }
  };

  // ============================================================
  // PRODUCT SUBTOTAL (qty > 1 のみリアルタイム小計表示)
  // ============================================================

  const initProductSubtotal = () => {
    document.querySelectorAll('.harmonia-subtotal').forEach(el => {
      const form = el.closest('form[data-product-id]');
      if (!form) return;

      const qtyInput = form.querySelector('.qty-adjuster__value');
      const variantSelector = form.querySelector('select[name="id"]');
      const productId = form.dataset.productId;
      const amountEl = el.querySelector('.harmonia-subtotal__amount');
      if (!qtyInput || !amountEl) return;

      // 商品JSONを取得（バリアント切替用）
      let productData = null;
      const productJsonEl = document.getElementById('ccProductJson-' + productId);
      if (productJsonEl) {
        try {
          productData = JSON.parse(productJsonEl.textContent);
        } catch (e) { /* ignore */ }
      }

      const updateSubtotal = () => {
        const qty = parseInt(qtyInput.value) || 1;
        const unitPrice = parseInt(el.dataset.unitPrice) || 0;

        if (qty > 1 && unitPrice > 0) {
          const subtotal = unitPrice * qty;
          amountEl.innerHTML = theme.formatMoney(subtotal);
          el.classList.add('harmonia-subtotal--visible');

          // zen-popIn アニメーション
          el.classList.remove('harmonia-subtotal--updating');
          void el.offsetWidth; // リフロー強制
          el.classList.add('harmonia-subtotal--updating');
          clearTimeout(el._subtotalAnimTimeout);
          el._subtotalAnimTimeout = setTimeout(() => {
            el.classList.remove('harmonia-subtotal--updating');
          }, 250);
        } else {
          el.classList.remove('harmonia-subtotal--visible');
        }
      };

      // 数量変更を監視
      // theme.js は jQuery .trigger('change') を使うため、
      // ネイティブ addEventListener では捕捉できない → jQuery で監視
      if (window.jQuery) {
        jQuery(qtyInput).on('change', updateSubtotal);
      } else {
        qtyInput.addEventListener('change', updateSubtotal);
      }
      // 直接入力にも対応
      qtyInput.addEventListener('input', updateSubtotal);

      // バリアント変更を監視（unit price を更新して再計算）
      if (variantSelector && productData) {
        variantSelector.addEventListener('change', () => {
          const variantId = parseInt(variantSelector.value);
          const variant = productData.variants.find(v => v.id === variantId);
          if (variant) {
            el.dataset.unitPrice = variant.price;
            updateSubtotal();
          }
        });
      }
    });
  };

  // ============================================================
  // iOS SAFARI CHECKOUT WORKAROUND
  // Shopify の Universal Checkout が POST /cart → 302 → shop.app へ
  // クロスオリジンリダイレクトするため、iOS Safari が「ダウンロードしますか？」
  // と誤認する問題への対策。
  // checkout ボタンの form POST を window.location.href = '/checkout' に
  // 置き換えることで、クロスオリジンPOSTリダイレクトを回避する。
  //
  // TODO: Shopify サポートに報告済みの場合、解決後にこのワークアラウンドを削除
  // 報告内容: POST /cart (checkout) → shop.app への 302 リダイレクトが
  // iOS Safari で「ダウンロード」ダイアログを引き起こす
  // ============================================================

  const initCheckoutWorkaround = () => {
    document.querySelectorAll('form[action$="/cart"], form[action="/cart"]').forEach(form => {
      form.addEventListener('submit', (e) => {
        const submitter = e.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        // 利用規約チェックボックスの検証（設定で有効な場合のみ存在）
        // e.preventDefault() 前にチェックし、未チェック時はネイティブ HTML5
        // バリデーションに任せる（required 属性によるブラウザ警告を表示）
        const termsCheckbox = form.querySelector('#terms');
        if (termsCheckbox && !termsCheckbox.checked) {
          return;
        }

        e.preventDefault();

        // ボタンにローディング状態を付与
        submitter.disabled = true;
        submitter.classList.add('btn--loading');

        // カートに商品があるか確認後、直接 /checkout へ GET 遷移
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            if (cart.item_count > 0) {
              // フォーム内のディスカウントコードをGETパラメータに転送（防御的）
              var discountInput = form.querySelector('[name="discount"]');
              var discount = discountInput ? discountInput.value.trim() : '';
              window.location.href = discount
                ? '/checkout?discount=' + encodeURIComponent(discount)
                : '/checkout';
            } else {
              // カートが空の場合はリロード（空カート表示に更新）
              submitter.disabled = false;
              submitter.classList.remove('btn--loading');
              window.location.href = '/cart';
            }
          })
          .catch(() => {
            // fetch 失敗時はフォールバックとして通常の form POST を実行
            submitter.disabled = false;
            submitter.classList.remove('btn--loading');
            form.submit();
          });
      });
    });
  };

  // ============================================================
  // AJAX FILTER RE-INITIALIZATION
  // Canopy の AJAX フィルタリングは [data-ajax-container] の innerHTML を
  // 置換するため、新しいカードに Harmonia インタラクションを再適用する
  // ============================================================

  const initAjaxObserver = () => {
    const ajaxContainers = document.querySelectorAll('[data-ajax-container]');
    if (!ajaxContainers.length) return;

    const observer = new MutationObserver((mutations) => {
      let hasNewCards = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (
            node.querySelector?.('.harmonia-product-card') ||
            node.classList?.contains('harmonia-stagger')
          )) {
            hasNewCards = true;
          }
        });
      });

      if (hasNewCards) {
        initProductCards();
        initWishlist();
        initQuickAddSpring();

        // スタガーアニメーション：子要素を個別監視
        document.querySelectorAll('.harmonia-stagger').forEach(container => {
          _observeStaggerChildren(container);
        });
      }
    });

    ajaxContainers.forEach(container => {
      observer.observe(container, { childList: true, subtree: true });
    });

    // Product Recommendations の AJAX 差し替えを監視
    // Canopy の theme.js が data-url を fetch → innerHTML を差し替えるため、
    // [data-ajax-container] とは別の監視が必要
    const recsContainer = document.querySelector('.product-recommendations');
    if (recsContainer) {
      const recsObserver = new MutationObserver((mutations) => {
        let hasNewCards = false;
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && node.querySelector?.('.harmonia-product-card')) {
              hasNewCards = true;
            }
          });
        });

        if (hasNewCards) {
          initProductCards();
          initWishlist();
          initQuickAddSpring();
        }
      });
      recsObserver.observe(recsContainer, { childList: true, subtree: true });
    }
  };

  // ============================================================
  // INITIALIZE ALL
  // ============================================================

  const init = () => {
    initScrollReveal();
    initProductCards();
    initWishlist();
    initButtonRipple();
    initSmoothScroll();
    initLazyImages();
    initQuantitySelectors();
    initZenQuantity();
    initProductSubtotal();
    initAccordions();
    initSpringButtons();
    initQuickAddSpring();
    initAjaxObserver();
    initCheckoutWorkaround();

    console.log('Harmonia Design System initialized (with Kokochiyoi Spring Motion)');
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.Harmonia = {
    init,
    initScrollReveal,
    initProductCards,
    initWishlist,
    initSpringButtons,
    initQuickAddSpring,
    initZenQuantity,
    initProductSubtotal,
    initAjaxObserver,
    initCheckoutWorkaround,
    springAnimate
  };

})();
